import React, { useState } from 'react';
import { Page } from 'ui/components/Page/Page';
import { CircularProgress, Typography } from '@mui/material';
import { colorPalette, IconNames } from 'ui/theme';
import { Icon } from 'ui/components/Icon';
import { useSelector } from 'react-redux';
import { getActiveUser } from 'services/user';
import { SplashPageTypes } from '../../types';

interface SplashPageProps {
  pageType: SplashPageTypes;
  statusMessage?: string;
}

const SplashPage: React.FC<SplashPageProps> = ({ pageType, statusMessage }) => {
  const [primaryMessage, setPrimaryMessage] = useState<string>('');
  const [secondaryMessage, setSecondaryMessage] = useState<string>('');
  const [graphic, setGraphic] = useState<JSX.Element | null>(null);

  const { user } = useSelector(getActiveUser);

  React.useEffect(() => {
    if (pageType === 'provisioning') {
      setPrimaryMessage('Your AI Insights account is being set up');
      setSecondaryMessage('This may take 5-10 minutes');
      setGraphic(
        <CircularProgress
          size={100}
          sx={{ color: colorPalette.redesign.lightPrimaryBlue }}
          data-qa="circular-spinner"
          aria-label="loading-spinner"
        />
      );
    } else if (pageType === 'error') {
      setPrimaryMessage('There was an error setting up your account');
      setSecondaryMessage(
        'Contact your account manager for assistance' +
          (statusMessage ? `. Error: ${statusMessage}` : '')
      );
      setGraphic(
        <Icon
          name={IconNames.FBOCloseCircle}
          height={100}
          width={100}
          fill={colorPalette.redesign.redPrimary}
          data-qa="error-icon"
          aria-label="error-icon"
        />
      );
    } else if (pageType === 'unauthorized') {
      setPrimaryMessage(
        `${user?.firstName || ''} ${
          user?.lastName || ''
        } has not been added for AI Insights`
      );
      setSecondaryMessage('Contact your account manager for access');
      setGraphic(
        <Icon
          name={IconNames.FBOAvatar}
          height={100}
          width={100}
          fill={colorPalette.redesign.yellowPrimary}
          data-qa="avatar-icon"
          aria-label="avatar-icon"
        />
      );
    }
  }, [pageType, user]);

  return (
    <Page
      margin="-16px"
      width="calc(100% + 32px);"
      height="calc(100% + 32px);"
      border="none"
      display={'flex'}
      flexDirection={'column'}
      alignItems={'center'}
      justifyContent={'center'}
      style={{ backgroundColor: colorPalette.white }}
    >
      {graphic}
      <Typography
        variant="h3"
        style={{
          margin: '40px 0 8px',
        }}
      >
        {primaryMessage}
      </Typography>
      <Typography
        variant="h4"
        style={{
          fontWeight: '400',
          color: colorPalette.redesign.contentSecondary,
        }}
      >
        {secondaryMessage}
      </Typography>
    </Page>
  );
};

export default SplashPage;
